"use client";

import { c as _c } from "react/compiler-runtime";
import ClientOnly from "library/ClientOnly";
import type { VideoField } from "library/sanity/assetMetadata";
import { useCombinedRefs } from "library/useCombinedRefs";
import { stegaClean } from "next-sanity";
import { useRef } from "react";
import ReactPlayer from "react-player";
import { Embed } from "data:text/javascript;base64,aW1wb3J0ICdAdmFuaWxsYS1leHRyYWN0L2Nzcy92YW5pbGxhLnZpcnR1YWwuY3NzP3ZlLWNzcz1RR3hoZVdWeUlISmxjMlYwT3cnOwppbXBvcnQgJ0B2YW5pbGxhLWV4dHJhY3QvY3NzL3ZhbmlsbGEudmlydHVhbC5jc3M/dmUtY3NzPVFHeGhlV1Z5SUdadmRXNWtZWFJwYjI0Nyc7CmltcG9ydCAnQHZhbmlsbGEtZXh0cmFjdC9jc3MvdmFuaWxsYS52aXJ0dWFsLmNzcz92ZS1jc3M9UUd4aGVXVnlJR3hwWW5KaGNuazcnOwppbXBvcnQgJ0B2YW5pbGxhLWV4dHJhY3QvY3NzL3ZhbmlsbGEudmlydHVhbC5jc3M/dmUtY3NzPVFHeGhlV1Z5SUdadmRXNWtZWFJwYjI0Nyc7CmltcG9ydCAnQHZhbmlsbGEtZXh0cmFjdC9jc3MvdmFuaWxsYS52aXJ0dWFsLmNzcz92ZS1jc3M9UUd4aGVXVnlJR1p2ZFc1a1lYUnBiMjRnZXdvZ0lEcHliMjkwSUhzS0lDQWdJQzB0Y1dkc1puQTNNRG9nTVRzS0lDQWdJQzB0Y1dkc1puQTNNVG9nTURzS0lDQWdJQzB0Y1dkc1puQTNNam9nTURzS0lDQWdJQzB0Y1dkc1puQTNNem9nTURzS0lDQjlDaUFnUUcxbFpHbGhJSE5qY21WbGJpQmhibVFnS0cxcGJpMTNhV1IwYURvZ056QXhjSGdwSUhzS0lDQWdJRHB5YjI5MElIc0tJQ0FnSUNBZ0xTMXhaMnhtY0Rjd09pQXdPd29nSUNBZ0lDQXRMWEZuYkdad056RTZJREU3Q2lBZ0lDQjlDaUFnZlFvZ0lFQnRaV1JwWVNCelkzSmxaVzRnWVc1a0lDaHRhVzR0ZDJsa2RHZzZJREV3TWpWd2VDa2dld29nSUNBZ09uSnZiM1FnZXdvZ0lDQWdJQ0F0TFhGbmJHWndOekU2SURBN0NpQWdJQ0FnSUMwdGNXZHNabkEzTWpvZ01Uc0tJQ0FnSUgwS0lDQjlDaUFnUUcxbFpHbGhJSE5qY21WbGJpQmhibVFnS0cxcGJpMTNhV1IwYURvZ01UWXdNWEI0S1NCN0NpQWdJQ0E2Y205dmRDQjdDaUFnSUNBZ0lDMHRjV2RzWm5BM01qb2dNRHNLSUNBZ0lDQWdMUzF4WjJ4bWNEY3pPaUF4T3dvZ0lDQWdmUW9nSUgwS2ZRJzsKaW1wb3J0ICdAdmFuaWxsYS1leHRyYWN0L2Nzcy92YW5pbGxhLnZpcnR1YWwuY3NzP3ZlLWNzcz1MbDgwZW5KcloyWXdJSHNLSUNBdExYTXRNRG9nYVdkdWIzSmxaRHNLZDJsa2RHZzZJREV3TUNVN0NtaGxhV2RvZERvZ01UQXdKVHNLTFMxdFpXUnBZUzF2WW1wbFkzUXRabWwwT2lCamIzWmxjanNLTFMxdFpXUnBZUzF2WW1wbFkzUXRjRzl6YVhScGIyNDZJR05sYm5SbGNqc0tPd3A5JzsKaW1wb3J0IHsgcnVudGltZVN0eWxlZCBhcyBfY2E3ODEgfSBmcm9tICdsaWJyYXJ5L3N0eWxlZC9ydW50aW1lJzsKZXhwb3J0IHZhciBFbWJlZCA9IF9jYTc4MSh7dGFnOidkaXYnLGN2YUJhc2U6J180enJrZ2YwJyxjdmFPcHRpb25zOnt9fSk7";
type VideoEmbedProps = Omit<React.ComponentPropsWithoutRef<typeof ReactPlayer>, "src" | "width" | "height"> & {
  ref?: React.Ref<HTMLVideoElement>;
  className?: string;
  video: VideoField;
  controls?: boolean;
  /** When true, seeks to 0 each time playback starts */
  resetOnPlay?: boolean;
};
function getVideoSrc(video: VideoField) {
  if (stegaClean(video.sourceType) === "mux") {
    const playbackId = video.muxVideo?.data?.playbackId;
    return {
      src: playbackId ? `https://stream.mux.com/${playbackId}.m3u8` : null,
      thumbnail: video.muxVideo?.data?.videoThumbnailUrl
    };
  }
  return {
    src: video.url ?? null,
    thumbnail: null
  };
}
/**
 * A React component for playing a variety of URLs, including file paths, HLS, DASH, YouTube, Vimeo, Wistia and Mux.
 * Accepts a Sanity `video` schema object directly. Fills its container — the parent is responsible for sizing.
 */
export function VideoEmbed(t0) {
  const $ = _c(22);
  let className;
  let externalRef;
  let onPlay;
  let props;
  let resetOnPlay;
  let t1;
  let video;
  if ($[0] !== t0) {
    ({
      className,
      video,
      controls: t1,
      resetOnPlay,
      onPlay,
      ref: externalRef,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = externalRef;
    $[3] = onPlay;
    $[4] = props;
    $[5] = resetOnPlay;
    $[6] = t1;
    $[7] = video;
  } else {
    className = $[1];
    externalRef = $[2];
    onPlay = $[3];
    props = $[4];
    resetOnPlay = $[5];
    t1 = $[6];
    video = $[7];
  }
  const controls = t1 === undefined ? true : t1;
  let t2;
  if ($[8] !== video) {
    t2 = getVideoSrc(video);
    $[8] = video;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const {
    src
  } = t2;
  const internalRef = useRef(null);
  const combinedRef = useCombinedRefs(externalRef, internalRef);
  if (!src) {
    return null;
  }
  let t3;
  if ($[10] !== onPlay || $[11] !== resetOnPlay) {
    t3 = resetOnPlay ? e => {
      const el = internalRef.current;
      if (el && el.currentTime > 0.1) {
        el.currentTime = 0;
      }
      onPlay?.(e);
    } : onPlay;
    $[10] = onPlay;
    $[11] = resetOnPlay;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== combinedRef || $[14] !== controls || $[15] !== props || $[16] !== src || $[17] !== t3) {
    t4 = <ReactPlayer ref={combinedRef} src={src} width="100%" height="100%" controls={controls} playsInline={true} onPlay={t3} {...props} />;
    $[13] = combinedRef;
    $[14] = controls;
    $[15] = props;
    $[16] = src;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== className || $[20] !== t4) {
    t5 = <ClientOnly><Embed className={className}>{t4}</Embed></ClientOnly>;
    $[19] = className;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
}